import { FC, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  Container,
  ContentLayout,
  Grid,
  Header,
  SpaceBetween
} from '@cloudscape-design/components';

import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges';
import ApprovalDetails from '../../../components/gcpCerts/ApprovalDetails';
import GcpCertApprovalForm from '../../../components/gcpCerts/ApprovalForm';
import GcpCertRejectionForm from '../../../components/gcpCerts/RejectionForm';
import LoadingSpinner from '../../../components/LoadingSpinner';
import CommentsOverview from '../../../components/Application/CommentsOverview';
import RequiredPermissions from '../../../helper/RequiredPermissions';
import CONSTANTS from '../../../constants';

import useUserPermissions from '../../../hooks/query/useUserPermissions';
import useSingleGcpCert from '../../../hooks/query/useSingleGcpCert';
import { GcpCert, GcpCertApproval } from '../../../types/gcpCert';

const GcpCertDetails: FC = () => {
  // ----- Hooks at top level -----
  const { id } = useParams<{ id?: string }>();
  const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])['idToken']['claims']['email'];

  const navigate = useNavigate();
  const [showApprovalForm, setShowApprovalForm] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  const { data: thisCert, isLoading } = useSingleGcpCert(id!); // assert id exists
  const { data: userPermissions } = useUserPermissions(CURRENT_USER_ID);

  // ----- Loading/Error States -----
  if (isLoading || !thisCert) return <LoadingSpinner />;

  if (thisCert.status === 'ERROR') {
    return (
      <Alert statusIconAriaLabel="Error" type="error" header="An error occurred">
        {thisCert.details?.message || 'Unable to load certificate details.'}
      </Alert>
    );
  }

  // ----- Helpers -----
  const hasPermission = (pattern: string): boolean =>
    userPermissions?.permissions?.some((perm: string) => perm.includes(pattern)) ?? false;

  const canApprove =
    hasPermission('tags:GCPTribe:*') || hasPermission('tags:PlatformOwner:*');

  const isRequestor =
    (thisCert.requestorID || '').toLowerCase() === (CURRENT_USER_ID || '').toLowerCase();
  const canApproveAsNonRequestor = canApprove && !isRequestor;

  const gcpCertDetails = mapGcpCertToDisplay(thisCert);

  // ----- Role determination for Approval Form -----
  const currentUserRole = (() => {
    if (thisCert.details.applicationOwnerEmail === CURRENT_USER_ID) return 'APPLICATION_OWNER';
    if (hasPermission('gcpCerts:*:approve') || hasPermission('gcpCerts:*:*') || hasPermission('*:*:*')) return 'GCP_TRIBE';
    return 'Viewer';
  })();

  // ----- Render -----
  return (
    <ContentLayout
      header={
        <HeaderWithStatusBadges
          headerName={thisCert.details.applicationName || thisCert.details.application || thisCert.details.applicationAlias || 'GCP Certificate'}
          status={thisCert.status}
        />
      }
    >
      <Grid
        gridDefinition={[
          { colspan: { default: 3, xxs: 9 } },
          { colspan: { default: 9, xxs: 3 } }
        ]}
      >
        <SpaceBetween direction="vertical" size="m">
          <CertificateDetails details={gcpCertDetails} />

          {/* ----- Action Buttons ----- */}
          {thisCert.status === 'PENDING_APPROVAL' && canApproveAsNonRequestor && (
            <RequiredPermissions requiredPermissions={CONSTANTS.GCP_CERT_APPROVER}>
              <Box float="right">
                <ButtonDropdown
                  items={[
                    { text: 'To Approve...', id: 'to-approve' },
                    { text: 'To Reject...', id: 'to-reject' }
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === 'to-approve') {
                      setShowApprovalForm(true);
                      setShowRejectionForm(false);
                    } else {
                      setShowApprovalForm(false);
                      setShowRejectionForm(true);
                    }
                  }}
                  variant="primary"
                >
                  Action
                </ButtonDropdown>
              </Box>
            </RequiredPermissions>
          )}

          {/* ----- Approval Form ----- */}
          {showApprovalForm && (
            <GcpCertApprovalForm
                gcpCert={thisCert}
                userPermissions={userPermissions?.permissions || []}
                onSubmit={() => setShowApprovalForm(false)}
            />
          )}

          {/* ----- Rejection Form ----- */}
          {showRejectionForm && (
            <GcpCertRejectionForm
              thisgcpCertID={thisCert.gcpCertID}
              shouldShowRejectionForm={setShowRejectionForm}
            />
          )}

          {/* ----- Rejected: Reopen opens full-page form (same layout as new request) ----- */}
          {thisCert.status === 'REJECTED' && (
            <Box padding="s">
              <Button variant="primary" onClick={() => navigate(`/gcpCerts/${thisCert.gcpCertID}/reopen`)}>
                Reopen request
              </Button>
            </Box>
          )}

          {/* ----- Approved Details ----- */}
          {thisCert.status === 'APPROVED' && (
            <RequiredPermissions requiredPermissions={CONSTANTS.GCP_CERT_APPROVER}>
                <Container header={<Header variant="h3">Approval Details</Header>}>
                    {(() => {
                      const approvedEntries = (thisCert.approvalStatus ?? []).filter(
                        (a: GcpCertApproval) => a.status === 'APPROVED'
                      );
                      const formatRoleName = (name: string) =>
                        name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                      if (approvedEntries.length > 0) {
                        return (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ borderBottom: '1px solid #d5dbdb' }}>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#414d54' }}>Role</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#414d54' }}>Approved by</th>
                                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: '#414d54' }}>Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {approvedEntries.map((a: GcpCertApproval) => (
                                <tr key={a.name} style={{ borderBottom: '1px solid #e9ebed' }}>
                                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{formatRoleName(a.name)}</td>
                                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>{a.approverID || '—'}</td>
                                  <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                    {a.lastUpdatedTime ? new Date(a.lastUpdatedTime).toLocaleString() : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }
                      return (
                        <SpaceBetween size="xs">
                          <Box padding="xs" className="bordered-field">
                            <strong>Approved by:</strong> {thisCert.approval?.approverID || 'N/A'}
                          </Box>
                          <Box padding="xs" className="bordered-field">
                            <strong>Approved date:</strong>{' '}
                            {thisCert?.lastUpdatedTime ? new Date(thisCert.lastUpdatedTime).toLocaleString() : 'N/A'}
                          </Box>
                        </SpaceBetween>
                      );
                    })()}
                </Container>
            </RequiredPermissions>
        )}      
        </SpaceBetween>

        {/* ----- Comments Section ----- */}
        <SpaceBetween direction="vertical" size="m">
          <Container header={<Header variant="h3">Comment History</Header>}>
            <CommentsOverview comments={thisCert.comments || []} />
          </Container>
        </SpaceBetween>
      </Grid>
    </ContentLayout>
  );
};

// ----- Certificate Details Component -----
const CertificateDetails = ({ details }: { details: Array<any> }) => (
  <Container header={<Header variant="h3">Certificate Details</Header>}>
    <SpaceBetween size="xs">
      {details.map((item, idx) => (
        <Box key={idx} padding="xs" className="bordered-field">
          <strong>{item.label}:</strong> {item.value}
        </Box>
      ))}
    </SpaceBetween>
  </Container>
);

// ----- Mapper -----
const mapGcpCertToDisplay = (cert: GcpCert) => [
  { label: 'Requestor', value: cert.requestorID },
  { label: 'Application Name', value: cert.details.applicationName || cert.details.application || cert.details.applicationAlias },
  { label: 'Environment', value: cert.details.environment },
  { label: 'Project ID', value: cert.details.projectId },
  { label: 'Application Owner', value: cert.details.applicationOwner },
  { label: 'Application Owner Email', value: cert.details.applicationOwnerEmail },
  { label: 'WBS Code', value: cert.details.wbsCode?.trim() || 'N/A' },
  { label: 'Cost Center', value: cert.details.costCenter?.trim() || 'N/A' },
  { label: 'DNS Domain', value: cert.details.dnsDomainName },
  {
    label: 'Certificate Request',
    value: (
      <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
        {(cert.details.dnsPrefixes || []).map((prefix: string, idx: number) => (
          <li key={idx}>{`${prefix}.${cert.details.dnsDomainName}`}</li>
        ))}
      </ol>
    )
  },
  { label: 'Created', value: new Date(cert.creationTime).toLocaleString() }
];

export default GcpCertDetails;